// Planificateur : participants, durée, date ; créneaux qui respectent tout le monde, moi compris.
import { label, type Contact } from '../core/contact';
import { buildIcs } from '../core/ics';
import { localTimesSummary, proposalText, spreadSlots } from '../core/planner';
import { meetingCandidates, type Slot } from '../core/schedule';
import { hm, nextDate, prettyDate, time, todayIn } from '../core/time';
import { google } from '../data/google';
import type { Store } from '../data/store';
import { download, el, esc } from './dom';
import { icon } from './icons';
import { showToast } from './toast';

let dialog: HTMLDialogElement | null = null;

function ensureDialog(): HTMLDialogElement {
  if (dialog && document.body.contains(dialog)) return dialog;
  dialog = document.createElement('dialog');
  dialog.id = 'planner';
  dialog.className = 'planner-dialog';
  dialog.setAttribute('aria-labelledby', 'plannerTitle');
  dialog.innerHTML = `<form method="dialog" novalidate>
    <header class="dlg-head"><div><div class="eyebrow">Trouver un créneau</div><h2 id="plannerTitle">Tout le monde à la même heure.</h2><p>Choisissez les participants, la date et la durée de l’appel.</p></div>
      <button class="icon-btn" type="button" data-close aria-label="Fermer">${icon('close')}</button></header>
    <div class="dlg-body">
      <div class="two">
        <div class="field"><label for="pl-date">Date <span class="muted">· mon fuseau</span></label><input id="pl-date" type="date" required></div>
        <div class="field"><label for="pl-duration">Durée de l’appel</label><select id="pl-duration">
          <option value="15">15 minutes</option><option value="30" selected>30 minutes</option><option value="45">45 minutes</option>
          <option value="60">1 heure</option><option value="90">1 h 30</option><option value="120">2 heures</option></select></div>
      </div>
      <div class="field"><div class="label-row"><label>Participants</label><button class="btn ghost small" type="button" data-toggle-all>Tout sélectionner</button></div><div class="chips" data-people></div></div>
      <label class="check-label"><input type="checkbox" data-include-me checked> Respecter aussi mes horaires <span class="muted" data-my-hours></span></label>
      <div class="field"><div class="label-row"><label>Créneaux communs</label><span class="hint" data-zone></span></div><div class="results" data-results aria-live="polite"></div></div>
      <p class="hint">Les propositions respectent les horaires déclarés de chacun. Les agendas réels et les jours fériés ne sont pas connus.</p>
    </div>
    <footer class="dlg-foot"><button class="btn" type="button" data-close>Fermer</button></footer></form>`;
  document.body.append(dialog);
  return dialog;
}

export interface PlannerOptions {
  /** Participants cochés à l’ouverture (sinon tous). */
  ids?: string[];
  date?: string;
  /** Ouvre le lien de proposition ; `meet` est le lien de visioconférence créé dans Google Agenda, s’il y en a un. */
  onPropose: (slots: Slot[], meet?: string) => void;
}

export function openPlanner(store: Store, opts: PlannerOptions): void {
  const d = ensureDialog();
  const me = store.state.profile;
  const people = store.active;
  if (!people.length) { showToast({ id: 0, kind: 'info', text: 'Ajoutez un contact pour planifier un premier appel.' }); return; }
  const dateEl = el<HTMLInputElement>(d, '#pl-date');
  const durationEl = el<HTMLSelectElement>(d, '#pl-duration');
  const peopleEl = el(d, '[data-people]');
  const includeMeEl = el<HTMLInputElement>(d, '[data-include-me]');
  const resultsEl = el(d, '[data-results]');
  let slots: Slot[] = [];

  dateEl.value = opts.date ?? todayIn(me.tz, Date.now());
  dateEl.min = todayIn(me.tz, Date.now());
  durationEl.value = '30';
  includeMeEl.checked = true;
  el(d, '[data-my-hours]').textContent = `(${hm(me.start)}–${hm(me.end)})`;
  el(d, '[data-zone]').textContent = `Heure de ${me.place}`;
  const preset = new Set(opts.ids ?? people.map((c) => c.id));
  peopleEl.innerHTML = people.map((c) => `<label class="chip"><input type="checkbox" value="${esc(c.id)}" ${preset.has(c.id) ? 'checked' : ''}>${esc(label(c))}${c.role === 'supplier' ? ' <span class="role-tag supplier">Fourn.</span>' : ''}</label>`).join('');

  function selected(): Contact[] {
    const ids = new Set([...peopleEl.querySelectorAll<HTMLInputElement>('input:checked')].map((i) => i.value));
    return people.filter((c) => ids.has(c.id));
  }

  function nextFullSlot(participants: Contact[], date: string, duration: number): { date: string; slot: Slot } | null {
    for (let k = 1; k <= 14; k++) {
      const dd = nextDate(date, k);
      const c = meetingCandidates({ people: participants, date: dd, duration, me: includeMeEl.checked ? me : null, tz: me.tz, allRequired: true, now: Date.now() });
      if (c.length) return { date: dd, slot: c[0]! };
    }
    return null;
  }

  function renderResults(): void {
    const participants = selected();
    const date = dateEl.value;
    const duration = Number(durationEl.value);
    slots = [];
    if (!participants.length) { resultsEl.innerHTML = '<p class="hint">Sélectionnez au moins un participant.</p>'; return; }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { resultsEl.innerHTML = '<p class="hint">Choisissez une date.</p>'; return; }
    let candidates: Slot[];
    try {
      candidates = meetingCandidates({ people: participants, date, duration, me: includeMeEl.checked ? me : null, tz: me.tz, allRequired: true, now: Date.now() });
    } catch (e) { resultsEl.innerHTML = `<p class="error">${esc(e instanceof Error ? e.message : 'Date invalide.')}</p>`; return; }
    slots = spreadSlots(candidates, 4);
    if (!slots.length) {
      const next = nextFullSlot(participants, date, duration);
      resultsEl.innerHTML = `<div class="empty" style="padding:18px 8px"><h3>Aucun créneau pour tout le monde ce jour-là.</h3><p style="margin-bottom:${next ? '12px' : '0'}">Essayez une autre date, un appel plus court ou moins de participants.</p>${next ? `<button class="btn small" type="button" data-goto="${esc(next.date)}">${icon('arrow')}Prochain créneau possible : ${esc(prettyDate(next.slot.start, me.tz))} ${esc(time(me.tz, next.slot.start))}</button>` : ''}</div>`;
      return;
    }
    resultsEl.innerHTML = slots.map((s, i) => `<div class="result">
        <div><strong class="num">${esc(time(me.tz, s.start))} – ${esc(time(me.tz, s.end))}</strong><small>${esc(localTimesSummary(s, time))} · ${duration} min${i === 0 ? ' · premier créneau' : ''}</small></div>
        <div class="result-actions"><button class="btn small primary" type="button" data-propose="${i}" title="Envoyer un lien à votre client">${icon('arrow')}<span>Proposer</span></button><button class="btn small" type="button" data-gcal="${i}" title="Créer dans Google Agenda avec un lien Meet, puis proposer">${icon('calendar')}<span>Agenda</span></button><button class="btn small" type="button" data-ics="${i}" aria-label="Télécharger le fichier calendrier" title="Télécharger un fichier .ics">${icon('download')}</button></div>
      </div>`).join('') + (slots.length > 1 ? `<button class="btn primary wide" type="button" data-propose-all>${icon('arrow')}Proposer ces ${slots.length} créneaux en un seul lien</button>` : '');
  }

  d.querySelector('form')!.onclick = (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-close]')) { d.close(); return; }
    const toggle = t.closest('[data-toggle-all]');
    if (toggle) { const inputs = [...peopleEl.querySelectorAll<HTMLInputElement>('input')]; const all = inputs.every((i) => i.checked); inputs.forEach((i) => { i.checked = !all; }); renderResults(); return; }
    const goto = t.closest<HTMLElement>('[data-goto]');
    if (goto) { dateEl.value = goto.dataset.goto!; renderResults(); return; }
    const pr = t.closest<HTMLElement>('[data-propose]');
    if (pr) { const s = slots[Number(pr.dataset.propose)]; if (s) { d.close(); opts.onPropose([s]); } return; }
    if (t.closest('[data-propose-all]')) { d.close(); opts.onPropose(slots.slice()); return; }
    const g = t.closest<HTMLButtonElement>('[data-gcal]');
    if (g) {
      const s = slots[Number(g.dataset.gcal)];
      if (!s) return;
      g.disabled = true;
      void google.createEvent({ start: s.start, end: s.end, tz: me.tz, title: 'Appel · ' + s.people.map((c) => label(c)).join(', '), description: proposalText(s, me), notify: false })
        .then((ev) => {
          showToast({ id: 0, kind: 'info', text: ev.meetUrl ? 'Événement créé dans Google Agenda, avec un lien Meet.' : 'Événement créé dans Google Agenda.' });
          d.close();
          opts.onPropose([s], ev.meetUrl ?? undefined);
        })
        .catch((e: unknown) => showToast({ id: 0, kind: 'error', text: e instanceof Error ? e.message : 'Création impossible.' }))
        .finally(() => { g.disabled = false; });
      return;
    }
    const ics = t.closest<HTMLElement>('[data-ics]');
    if (ics) {
      const s = slots[Number(ics.dataset.ics)];
      if (!s) return;
      const text = buildIcs({ uid: `a5w-${s.start}-${s.end}-${s.people.map((c) => c.id).sort().join('.')}@acte-5.com`, start: s.start, end: s.end, summary: 'Appel · ' + s.people.map((c) => label(c)).join(', '), description: proposalText(s, me), now: Date.now() });
      download(`appel-${todayIn(me.tz, s.start)}.ics`, text, 'text/calendar;charset=utf-8');
      showToast({ id: 0, kind: 'info', text: 'Fichier calendrier téléchargé. Ouvrez-le dans votre agenda.' });
    }
  };
  d.querySelector('form')!.onsubmit = (e) => e.preventDefault();
  dateEl.onchange = durationEl.onchange = includeMeEl.onchange = renderResults;
  peopleEl.onchange = renderResults;

  renderResults();
  d.showModal();
}
